import { forwardRef, type ReactNode, type Ref } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './List.css';

export interface ListProps extends BaseProps {
  type?: 'ordered' | 'unordered';
  icon?: ReactNode;
  spacing?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  withPadding?: boolean;
  children?: ReactNode;
}

export interface ListItemProps extends BaseProps {
  icon?: ReactNode;
  children?: ReactNode;
}

const ListItem = forwardRef(function ListItem(
  { icon, children, ...rest }: ListItemProps,
  ref: Ref<HTMLLIElement>
): ReactNode {
  const base = useBaseAttrs('rr-list__item', rest);
  const { dom } = splitBaseProps(rest);
  return (
    <li {...dom} ref={ref} {...base.attrs} className={base.className} style={base.style}>
      {icon ? <span className="rr-list__icon">{icon}</span> : null}
      <span className="rr-list__body">{children}</span>
    </li>
  );
});

interface ListComponent
  extends React.ForwardRefExoticComponent<ListProps & React.RefAttributes<HTMLUListElement | HTMLOListElement>> {
  Item: typeof ListItem;
}

export const List = forwardRef(function List(
  { type = 'unordered', icon, spacing = 'xs', withPadding, children, ...rest }: ListProps,
  ref: Ref<HTMLUListElement | HTMLOListElement>
): ReactNode {
  const Tag = type === 'ordered' ? 'ol' : 'ul';
  const base = useBaseAttrs('rr-list', rest, [
    `rr-list--${type}`,
    !!icon && 'rr-list--with-icon',
    !!withPadding && 'rr-list--padded'
  ]);
  const { dom } = splitBaseProps(rest);
  return (
    <Tag {...dom}
      ref={ref as Ref<HTMLUListElement & HTMLOListElement>}
      {...base.attrs}
      className={base.className}
      style={{
        ...base.style,
        ['--rr-list-gap' as string]: `var(--rr-spacing-${spacing})`
      }}
    >
      {children}
    </Tag>
  );
}) as ListComponent;

List.Item = ListItem;
